import { useEffect, useState } from 'react';
import { callApi } from '../api';
import { Section, Stat, ErrorBox, LoadingSpinner } from '../components/Section';
import { useSession } from '../hooks/useSession';
import { timeAgo } from '../utils';

const COST_WINDOWS = [
  { value: 7, label: 'last 7 days' },
  { value: 30, label: 'last 30 days' },
  { value: 90, label: 'last 90 days' },
  { value: 3650, label: 'all time' },
];

export function SystemHealth() {
  const { session } = useSession();
  const [accounts, setAccounts] = useState(null);
  const [costs, setCosts] = useState(null);
  const [costDays, setCostDays] = useState(30);
  const [coverage, setCoverage] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      callApi('/api/v1/admin/system/beli-accounts', { accessToken: session.access_token }),
      callApi('/api/v1/admin/system/embeddings', { accessToken: session.access_token }),
    ])
      .then(([a, e]) => {
        setAccounts(a);
        setCoverage(e);
      })
      .catch((e) => setError(e.message));
  }, [session.access_token]);

  useEffect(() => {
    callApi(`/api/v1/admin/system/costs?days=${costDays}`, {
      accessToken: session.access_token,
    })
      .then(setCosts)
      .catch((e) => setError(e.message));
  }, [costDays, session.access_token]);

  if (error) return <ErrorBox message={error} />;
  if (!accounts || !costs || !coverage) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-stone-800">System health</h1>

      <Section title={`Beli accounts (${accounts.accounts.length})`}>
        {accounts.accounts.length === 0 ? (
          <p className="text-sm text-stone-500">No accounts configured.</p>
        ) : (
          <div className="divide-y">
            {accounts.accounts.map((a) => (
              <div key={a.id} className="flex items-start justify-between py-2">
                <div className="flex gap-3 items-start">
                  <span
                    className={`w-3 h-3 rounded-full mt-1 ${trafficLightColor(a)}`}
                    title={trafficLightLabel(a)}
                  />
                  <div className="text-sm">
                    <div className="font-medium">
                      Account #{a.id} {a.admin_display_name && `· ${a.admin_display_name}`}
                    </div>
                    <div className="text-xs text-stone-500 mt-0.5">
                      {a.daily_count} calls today · {a.error_count} total errors ·{' '}
                      last used {a.last_used_at ? timeAgo(a.last_used_at) : 'never'}
                    </div>
                    {a.last_error && (
                      <div className="text-xs text-red-600 mt-1 line-clamp-2">
                        last error {a.last_error_at ? timeAgo(a.last_error_at) : ''}: {a.last_error}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section
        title="Costs"
        action={
          <select
            value={costDays}
            onChange={(e) => setCostDays(parseInt(e.target.value, 10))}
            className="text-sm border rounded px-2 py-1"
          >
            {COST_WINDOWS.map((w) => (
              <option key={w.value} value={w.value}>{w.label}</option>
            ))}
          </select>
        }
      >
        <CostsChart rows={costs.rows} />
      </Section>

      <Section title="Embeddings coverage">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat value={coverage.restaurants_total} label="restaurants" />
          <Stat
            value={coverage.restaurants_with_embedding}
            label="w/ embedding"
          />
          <Stat value={coverage.users_total} label="users" />
          <Stat value={coverage.users_with_taste_profile} label="taste profiles" />
        </div>
      </Section>
    </div>
  );
}

// Traffic-light: red = dead, amber = had an error in the last 24h,
// green = otherwise. Cumulative `error_count` is NOT a good signal because
// it never resets — an account that errored once 6 months ago would stay
// amber forever. last_error_at is the recency we actually care about.
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function trafficLightColor(a) {
  if (a.is_dead) return 'bg-red-500';
  if (a.last_error_at && Date.now() - new Date(a.last_error_at).getTime() < ONE_DAY_MS) {
    return 'bg-amber-500';
  }
  return 'bg-green-500';
}

function trafficLightLabel(a) {
  if (a.is_dead) return 'Dead — needs token rotation';
  if (a.last_error_at && Date.now() - new Date(a.last_error_at).getTime() < ONE_DAY_MS) {
    return 'Had an error in the last 24h';
  }
  return 'Healthy';
}

function CostsChart({ rows }) {
  if (!rows || rows.length === 0) return <p className="text-sm text-stone-500">No cost data yet.</p>;
  const max = Math.max(1, ...rows.map((r) => Number(r.estimated_cost_usd) || 0));
  return (
    <div className="space-y-1">
      {rows.slice(-30).map((r) => (
        <div key={r.day} className="flex items-center gap-2 text-xs">
          <span className="w-24 text-stone-500">{r.day}</span>
          <div className="flex-1 bg-stone-100 rounded h-3 relative">
            <div
              className="absolute inset-y-0 left-0 bg-stone-700 rounded"
              style={{ width: `${(Number(r.estimated_cost_usd) / max) * 100}%` }}
            />
          </div>
          <span className="w-20 text-right text-stone-700">
            ${Number(r.estimated_cost_usd).toFixed(2)}
          </span>
        </div>
      ))}
    </div>
  );
}
